/* ============================================================
   A. Vaccaro — theme.js
   Nav, accordions, Shopify AJAX cart, product variant picker
   ============================================================ */

const money = (cents) => {
  const n = (cents / 100);
  return '$ ' + n.toLocaleString(undefined, {
    minimumFractionDigits: n % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2
  });
};

/* ── Toast ── */
let toastTimer;
function showToast(msg) {
  let el = document.getElementById('toast');
  if (!el) {
    el = document.createElement('div');
    el.id = 'toast';
    el.className = 'toast';
    el.setAttribute('role', 'status');
    document.body.appendChild(el);
  }
  el.textContent = msg;
  setTimeout(() => el.classList.add('show'), 20);
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el.classList.remove('show'), 3500);
}

/* ── Nav: shop dropdown ── */
const shopDropdown = document.getElementById('shopDropdown');
const shopTrigger = document.getElementById('shopTrigger');
if (shopTrigger && shopDropdown) {
  shopTrigger.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    shopDropdown.classList.toggle('open');
  });
  document.addEventListener('click', (e) => {
    if (!e.target.closest('.nav-dropdown')) shopDropdown.classList.remove('open');
  });
}

/* ── Nav: scroll border ── */
const navEl = document.querySelector('nav');
if (navEl) {
  const onScroll = () => navEl.classList.toggle('scrolled', window.scrollY > 0);
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

/* ── Accordions (Details / Product Care) ── */
document.querySelectorAll('.product-details-trigger').forEach(btn => {
  btn.addEventListener('click', () => {
    const wrap = btn.closest('.product-details');
    wrap.classList.toggle('open');
    btn.querySelector('.product-details-icon').textContent =
      wrap.classList.contains('open') ? '−' : '+';
  });
});

/* ── FAQ accordion ── */
document.querySelectorAll('.faq-q').forEach(btn => {
  btn.addEventListener('click', () => {
    const item = btn.closest('.faq-item');
    item.classList.toggle('open');
    btn.querySelector('.faq-icon').textContent =
      item.classList.contains('open') ? '−' : '+';
  });
});

/* ── Cart drawer ── */
const cartDrawer = document.getElementById('cartDrawer');
const cartOverlay = document.getElementById('cartOverlay');

function cartOpen() {
  cartDrawer?.classList.add('open');
  cartOverlay?.classList.add('open');
  cartDrawer?.setAttribute('aria-hidden', 'false');
}
function cartClose() {
  cartDrawer?.classList.remove('open');
  cartOverlay?.classList.remove('open');
  cartDrawer?.setAttribute('aria-hidden', 'true');
}

cartOverlay?.addEventListener('click', cartClose);
document.getElementById('cartClose')?.addEventListener('click', cartClose);
document.querySelectorAll('.nav-cart, [data-cart-toggle]').forEach(el => {
  el.addEventListener('click', (e) => { e.preventDefault(); cartOpen(); renderCart(); });
});

async function fetchCart() {
  const res = await fetch(`${window.routes.cart}.js`, { headers: { 'Accept': 'application/json' } });
  return res.json();
}

async function cartAdd(variantId, qty = 1) {
  const res = await fetch(window.routes.cart_add, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ items: [{ id: variantId, quantity: qty }] })
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    showToast(err.description || 'Could not add to cart.');
    return null;
  }
  await renderCart();
  cartOpen();
  return res.json();
}

async function cartChange(key, qty) {
  await fetch(window.routes.cart_change, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ id: key, quantity: qty })
  });
  await renderCart();
}

function updateCartCount(count) {
  document.querySelectorAll('.nav-cart').forEach(el => {
    el.textContent = `${window.cartLabel || 'Cart'} (${count})`;
  });
}

async function renderCart() {
  const cart = await fetchCart();
  updateCartCount(cart.item_count);

  const list = document.getElementById('cartItems');
  const footer = document.getElementById('cartFooter');
  if (!list) return;

  if (cart.item_count === 0) {
    list.innerHTML = `<div class="cart-empty">${window.cartEmptyText || 'Your cart is empty.'}</div>`;
    if (footer) footer.hidden = true;
    return;
  }

  list.innerHTML = cart.items.map(it => `
    <div class="cart-item">
      <img class="cart-item-img" src="${it.image ? it.image.replace(/(\.[^.]+)$/, '_160x200_crop_center$1') : ''}" alt="${it.product_title}">
      <div class="cart-item-info">
        <div class="cart-item-name">${it.product_title}</div>
        <div class="cart-item-meta">${it.options_with_values.map(o => o.value).join(' · ')}</div>
        <div class="cart-item-meta">${money(it.final_price)}</div>
        <div class="cart-item-row">
          <div class="cart-qty">
            <button data-key="${it.key}" data-qty="${it.quantity - 1}" aria-label="Decrease quantity">−</button>
            <span>${it.quantity}</span>
            <button data-key="${it.key}" data-qty="${it.quantity + 1}" aria-label="Increase quantity">+</button>
          </div>
          <button class="cart-item-remove" data-key="${it.key}" data-qty="0">Remove</button>
        </div>
      </div>
    </div>
  `).join('');

  list.querySelectorAll('button[data-key]').forEach(btn => {
    btn.addEventListener('click', () => cartChange(btn.dataset.key, Number(btn.dataset.qty)));
  });

  const sub = document.getElementById('cartSubtotal');
  if (sub) sub.textContent = money(cart.items_subtotal_price);
  if (footer) footer.hidden = false;
}

document.getElementById('cartCheckout')?.addEventListener('click', () => {
  window.location.href = '/checkout';
});

/* ── Product page: variant picker ── */
const productForm = document.getElementById('productForm');
if (productForm) {
  const dataEl = document.getElementById('productVariants');
  const variants = dataEl ? JSON.parse(dataEl.textContent) : [];
  const copyEl = document.getElementById('variantCopy');
  const copy = copyEl ? JSON.parse(copyEl.textContent) : {};

  const priceText = document.getElementById('priceText');
  const descEl = document.getElementById('description');
  const detailsEl = document.getElementById('detailsList');
  const careEl = document.getElementById('careList');
  const addBtn = document.getElementById('addToBag');
  const variantInput = document.getElementById('selectedVariantId');

  const state = { material: null, size: null, generation: null };

  // Default material = first available option1
  const materials = [...new Set(variants.map(v => v.option1))];
  const sizes = [...new Set(variants.map(v => v.option2).filter(Boolean))];
  const generations = [...new Set(variants.map(v => v.option3).filter(Boolean))];
  state.material = materials[0];
  state.size = null;
  state.generation = null;

  const waitlistForm = document.getElementById('waitlistForm');
  const waitlistTags = document.getElementById('waitlistTags');
  if (waitlistForm) {
    waitlistForm.addEventListener('submit', () => {
      try { sessionStorage.setItem('wl_submitted', '1'); } catch (e) {}
    });
  }
  const productHandle = (document.body.dataset.productHandle) ||
    (window.location.pathname.split('/products/')[1] || 'product').replace(/[\/?#].*$/, '');

  function findVariant() {
    return variants.find(v =>
      v.option1 === state.material &&
      (sizes.length ? v.option2 === state.size : true) &&
      (generations.length ? v.option3 === state.generation : true)
    );
  }

  function applyCopy(material) {
    const matBullet = document.getElementById('materialBullet');
    if (matBullet) {
      const m = (material || '').toLowerCase();
      if (m.includes('gold')) {
        matBullet.textContent = 'Crafted in solid 10k gold';
      } else {
        matBullet.textContent = 'Crafted in .925 sterling silver';
      }
    }
    const c = copy[material];
    if (!c) return;
    if (descEl && c.description) descEl.textContent = c.description;
    if (detailsEl && c.details) detailsEl.innerHTML = c.details.map(d => `<li>${d}</li>`).join('');
    if (careEl && c.care) careEl.innerHTML = c.care.map(d => `<li>${d}</li>`).join('');
  }

  function sizeExists(size) {
    return variants.some(v =>
      v.option1 === state.material &&
      (generations.length && state.generation ? v.option3 === state.generation : true) &&
      v.option2 === size
    );
  }

  function updateSizeAvailability() {
    document.querySelectorAll('.size-item').forEach(btn => {
      const size = btn.dataset.size;
      const exists = sizeExists(size);
      btn.disabled = !exists;
      btn.classList.toggle('unavailable', !exists);
      if (!exists && state.size === size) {
        state.size = null;
        btn.classList.remove('selected');
      }
    });
  }

  function refresh() {
    updateSizeAvailability();
    const v = findVariant();
    if (priceText) {
      if (v) {
        priceText.textContent = money(v.price);
      } else {
        const prices = variants.filter(x => x.option1 === state.material).map(x => x.price);
        if (prices.length) {
          const min = Math.min(...prices), max = Math.max(...prices);
          priceText.textContent = min === max ? money(min) : `${money(min)} – ${money(max)}`;
        }
      }
    }
    const variantUnavailable = v && !v.available;
    if (waitlistForm) {
      if (variantUnavailable) {
        waitlistForm.classList.add('show');
        addBtn.style.display = 'none';
        const waitlistGenLabel = document.getElementById('waitlistGen');
        const waitlistSizeNote = document.getElementById('waitlistSizeNote');
        const waitlistSubmit = document.getElementById('waitlistSubmit');
        const waitlistBody = document.getElementById('waitlistBody');
        if (waitlistGenLabel && state.generation) waitlistGenLabel.textContent = state.generation;
        if (waitlistSizeNote) waitlistSizeNote.style.display = state.size ? 'none' : '';
        if (waitlistSubmit) waitlistSubmit.disabled = !state.size;
        if (waitlistTags) {
          const parts = ['waitlist', productHandle];
          if (state.generation) parts.push(state.generation.toLowerCase().replace(/\s+/g, '-'));
          if (state.size) parts.push('size-' + String(state.size).replace(/\s+/g, '-'));
          waitlistTags.value = parts.join(',');
        }
        if (waitlistBody) {
          const lines = [];
          lines.push('Product: ' + (document.querySelector('.product-title')?.textContent.trim() || productHandle));
          if (state.generation) lines.push('Generation: ' + state.generation);
          if (state.size) lines.push('Size: ' + state.size);
          if (state.material) lines.push('Material: ' + state.material);
          waitlistBody.value = lines.join('\n');
        }
      } else {
        waitlistForm.classList.remove('show');
        addBtn.style.display = '';
      }
    }
    if (v && v.available) {
      addBtn.disabled = false;
      addBtn.textContent = `Add to Cart — ${money(v.price)}`;
      variantInput.value = v.id;
    } else {
      addBtn.disabled = true;
      if (generations.length && !state.generation) {
        addBtn.textContent = 'Select Generation';
      } else if (sizes.length && !state.size) {
        addBtn.textContent = 'Select Size';
      } else {
        addBtn.textContent = 'Add to Cart';
      }
    }
  }

  document.querySelectorAll('.material-option').forEach(s => {
    s.addEventListener('click', () => {
      document.querySelectorAll('.material-option').forEach(x => x.classList.remove('selected'));
      s.classList.add('selected');
      state.material = s.dataset.material;
      applyCopy(state.material);
      refresh();
    });
  });

  document.querySelectorAll('.generation-btn').forEach(b => {
    b.addEventListener('click', () => {
      document.querySelectorAll('.generation-btn').forEach(x => x.classList.remove('selected'));
      b.classList.add('selected');
      state.generation = b.dataset.generation;
      refresh();
    });
  });

  document.querySelectorAll('.size-item').forEach(btn => {
    btn.addEventListener('click', () => {
      if (btn.disabled || btn.classList.contains('unavailable')) return;
      document.querySelectorAll('.size-item').forEach(x => x.classList.remove('selected'));
      btn.classList.add('selected');
      state.size = btn.dataset.size;
      refresh();
    });
  });

  productForm.addEventListener('submit', (e) => {
    e.preventDefault();
    if (generations.length && !state.generation) return;
    if (sizes.length && !state.size) return;
    const v = findVariant();
    if (!v || !v.available) return;
    cartAdd(v.id, 1);
  });

  applyCopy(state.material);
  refresh();
}

/* ── Newsletter: inline confirmation on Shopify customer form ── */
/* On success Shopify reloads and footer.liquid renders the inline
   "Thanks…" message (form.posted_successfully?). JS only handles the
   client-side invalid-email case, also inline. */
document.querySelectorAll('.footer-col-form').forEach(form => {
  form.addEventListener('submit', (e) => {
    const input = form.querySelector('input[type="email"]');
    const email = input?.value.trim();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      e.preventDefault();
      let el = form.querySelector('.footer-col-msg');
      if (!el) {
        el = document.createElement('p');
        el.className = 'footer-col-msg';
        el.setAttribute('role', 'status');
        el.setAttribute('aria-live', 'polite');
        form.appendChild(el);
      }
      el.textContent = 'Please enter a valid email.';
      void el.offsetWidth;
      el.classList.add('show');
    }
  });
});

/* ── Product image carousel dots (mobile) ── */
(function () {
  const track = document.querySelector('.product-images');
  const dotsWrap = document.querySelector('.product-dots');
  if (!track || !dotsWrap) return;
  const slides = Array.from(track.children);
  const dots = Array.from(dotsWrap.children);
  if (slides.length < 2 || dots.length !== slides.length) return;

  const setActive = (i) => dots.forEach((d, n) => d.classList.toggle('active', n === i));
  setActive(0);

  dots.forEach((dot, i) => {
    dot.addEventListener('click', () => {
      slides[i].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    });
  });

  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (e.isIntersecting) {
        const idx = slides.indexOf(e.target);
        if (idx > -1) setActive(idx);
      }
    });
  }, { root: track, threshold: 0.6 });
  slides.forEach((s) => io.observe(s));
})();

/* ── Initial cart count ── */
renderCart();

/* ── Newsletter popup ── */
(function () {
  const overlay = document.getElementById('newsletterPopup');
  if (!overlay) return;
  const KEY = 'np_dismissed_v1';
  const closeBtn = overlay.querySelector('.np-close');
  const form = overlay.querySelector('.np-form');
  const successMsg = overlay.querySelector('.np-msg.show');

  function open() {
    overlay.hidden = false;
    void overlay.offsetWidth;
    overlay.classList.add('open');
    overlay.setAttribute('aria-hidden', 'false');
  }
  function close() {
    overlay.classList.remove('open');
    overlay.setAttribute('aria-hidden', 'true');
    try { localStorage.setItem(KEY, '1'); } catch (e) {}
    setTimeout(() => { overlay.hidden = true; }, 300);
  }

  let dismissed = false;
  try { dismissed = localStorage.getItem(KEY) === '1'; } catch (e) {}

  let waitlistJustSubmitted = false;
  try { waitlistJustSubmitted = sessionStorage.getItem('wl_submitted') === '1'; } catch (e) {}
  if (waitlistJustSubmitted) {
    try { sessionStorage.removeItem('wl_submitted'); } catch (e) {}
  }

  if (successMsg && !waitlistJustSubmitted) {
    open();
    try { localStorage.setItem(KEY, '1'); } catch (e) {}
  } else if (!successMsg && !dismissed) {
    const delay = (parseInt(overlay.dataset.delay, 10) || 4) * 1000;
    setTimeout(open, delay);
  }

  if (closeBtn) closeBtn.addEventListener('click', close);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !overlay.hidden) close(); });

  if (form) {
    form.addEventListener('submit', (e) => {
      const input = form.querySelector('input[type="email"]');
      const email = input && input.value.trim();
      if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
        e.preventDefault();
        let msg = form.querySelector('.np-msg');
        if (!msg) {
          msg = document.createElement('p');
          msg.className = 'np-msg';
          msg.setAttribute('role', 'status');
          msg.setAttribute('aria-live', 'polite');
          form.appendChild(msg);
        }
        msg.textContent = 'Please enter a valid email.';
        void msg.offsetWidth;
        msg.classList.add('show');
        return;
      }
      try { localStorage.setItem(KEY, '1'); } catch (e) {}
    });
  }
})();


/* ── Shipping banner ── */
(function () {
  const banner = document.getElementById('shippingBanner');
  if (!banner) return;
  const KEY = 'sb_dismissed_v2';
  const closeBtn = banner.querySelector('.shipping-banner-close');
  let dismissed = false;
  try { dismissed = localStorage.getItem(KEY) === '1'; } catch (e) {}
  if (!dismissed) {
    banner.hidden = false;
    document.body.classList.add('has-banner');
  }
  if (closeBtn) {
    closeBtn.addEventListener('click', () => {
      document.body.classList.remove('has-banner');
      try { localStorage.setItem(KEY, '1'); } catch (e) {}
      banner.hidden = true;
    });
  }

  if (!dismissed) {
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        if (window.scrollY > 40) {
          document.body.classList.add('banner-hidden');
        } else {
          document.body.classList.remove('banner-hidden');
        }
        ticking = false;
      });
    }, { passive: true });
  }
})();


/* ── Product image lightbox ── */
(function () {
  const lightbox = document.getElementById('ImageLightbox');
  if (!lightbox) return;
  const img = lightbox.querySelector('.lightbox-img');
  const closeBtn = lightbox.querySelector('.lightbox-close');
  const triggers = document.querySelectorAll('.product-img-zoom');

  function open(src) {
    img.src = src;
    lightbox.classList.add('open');
    lightbox.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }
  function close() {
    lightbox.classList.remove('open');
    lightbox.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    img.src = '';
  }

  triggers.forEach((btn) => {
    btn.addEventListener('click', () => {
      const src = btn.getAttribute('data-zoom-src');
      if (src) open(src);
    });
  });
  lightbox.addEventListener('click', (e) => {
    if (e.target === lightbox || e.target === img) close();
  });
  closeBtn.addEventListener('click', close);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && lightbox.classList.contains('open')) close();
  });
})();


/* ── Product image gallery (thumbnail switcher + click-through) ── */
(function () {
  const thumbs = Array.from(document.querySelectorAll('.product-thumb'));
  const mainImg = document.getElementById('ProductMainImg');
  const indexEl = document.getElementById('ProductImgIndex');
  const prevBtn = document.getElementById('ProductNavPrev');
  const nextBtn = document.getElementById('ProductNavNext');
  if (!thumbs.length || !mainImg) return;

  let current = 0;

  function setIndex(i) {
    if (thumbs.length === 0) return;
    current = (i + thumbs.length) % thumbs.length;
    const btn = thumbs[current];
    const src = btn.getAttribute('data-main-src');
    if (src) mainImg.src = src;
    if (indexEl) indexEl.textContent = String(current + 1);
    thumbs.forEach((t) => t.classList.remove('selected'));
    btn.classList.add('selected');
  }

  thumbs.forEach((btn, i) => {
    btn.addEventListener('click', () => setIndex(i));
  });
  if (prevBtn) prevBtn.addEventListener('click', () => setIndex(current - 1));
  if (nextBtn) nextBtn.addEventListener('click', () => setIndex(current + 1));

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    const t = e.target;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
    if (e.key === 'ArrowLeft') setIndex(current - 1);
    if (e.key === 'ArrowRight') setIndex(current + 1);
  });
})();


/* ── Product mobile carousel counter + dots ── */
(function () {
  const carousel = document.getElementById('ProductCarousel');
  if (!carousel) return;
  const indexEl = document.getElementById('ProductCarouselIndex');
  const imgs = carousel.querySelectorAll('.product-carousel-img');
  if (!imgs.length) return;
  const dots = document.querySelectorAll('.product-dot');

  function setActive(i) {
    if (indexEl) indexEl.textContent = String(i + 1);
    dots.forEach((d, di) => d.classList.toggle('active', di === i));
  }
  setActive(0);

  dots.forEach((d, di) => {
    d.addEventListener('click', () => {
      carousel.scrollTo({ left: di * carousel.clientWidth, behavior: 'smooth' });
    });
  });

  let ticking = false;
  carousel.addEventListener('scroll', () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      const w = carousel.clientWidth;
      const i = Math.max(0, Math.min(imgs.length - 1, Math.round(carousel.scrollLeft / w)));
      setActive(i);
      ticking = false;
    });
  }, { passive: true });
})();
